<template>
  <div id="item">
    <v-dialog v-model="report.modal" max-width="600px">
      <v-card class="rounded-xl" elevation="7">
        <v-card-title class="text-h5">Reporting upload.</v-card-title>
        <v-card-subtitle
          >Please submit this form if you believe this upload violates
          {{ $store.state.site.name }} Terms of Service.
        </v-card-subtitle>
        <v-card-text>
          <v-container>
            <v-text-field
              class="rounded-xl"
              v-model="report.message"
              label="Tell us why."
              required
            ></v-text-field>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            class="rounded-xl"
            color="red darken-1"
            text
            @click="doReport()"
            @loading="report.loading"
            >Report
          </v-btn>
          <v-btn
            class="rounded-xl"
            color="blue darken-1"
            text
            @click="report.modal = false"
            >Cancel
          </v-btn>
          <v-spacer></v-spacer>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-container v-if="!invalid">
      <v-card class="rounded-xl" elevation="7">
        <v-toolbar>
          <v-toolbar-title>{{ name }}</v-toolbar-title>
        </v-toolbar>
        <v-container
          v-if="!loading"
          class="text-center"
          style="white-space: pre-line; overflow-wrap: anywhere"
        >
          <v-img
            v-if="type === 'image'"
            :src="'https://i.troplo.com/i/' + attachment"
            contain
            height="450"
          ></v-img>
          <video v-if="type === 'video'" controls height="350">
            <source :src="'/i/' + attachment" type="video/mp4" />
          </video>
          <br v-if="type === 'video'" />
          <p
            v-if="type !== 'image' && type !== 'video' && type !== 'paste'"
            class="text-h4"
          >
            Preview Unavailable
          </p>
          <highlightjs
            v-if="
              type === 'paste' &&
              data.paste.highlighting !== 'none' &&
              data.paste.highlighting !== 'auto'
            "
            :code="data.paste.content"
            :language="data.paste.highlighting"
            class="text-left"
          />
          <highlightjs
            v-if="type === 'paste' && data.paste.highlighting === 'auto'"
            :code="data.paste.content"
            autodetect
            class="text-left"
          />
          <highlightjs
            v-if="type === 'paste' && data.paste.highlighting === 'none'"
            :code="data.paste.content"
            class="text-left"
          />
          <span v-if="user" class="text-sm-h5">
            <template v-if="type !== 'paste'">Uploaded by: </template>
            <template v-if="type === 'paste'">Paste created by: </template>
            <v-icon
              v-if="
                user.plan.internalName !== 'FREE' &&
                user.plan.internalName.indexOf('CSTM_TIER_') >= 0
              "
              :color="user.plan.color"
              aria-hidden="false"
            >
              {{ user.plan.icon }}
            </v-icon>
            <v-icon
              v-if="
                user.administrator &&
                user.plan.internalName.indexOf('CSTM_TIER_')
              "
              aria-hidden="false"
              color="red"
            >
              mdi-gavel
            </v-icon>
            <v-icon
              v-if="
                user.plan.internalName !== 'FREE' &&
                !user.administrator &&
                !user.moderator &&
                user.plan.internalName.indexOf('CSTM_TIER_')
              "
              :color="user.plan.color"
              aria-hidden="false"
            >
              {{ user.plan.icon }}
            </v-icon>
            <span
              v-if="
                user.beta &&
                user.plan.internalName === 'FREE' &&
                !user.administrator &&
                !user.moderator &&
                user.plan.internalName.indexOf('CSTM_TIER_')
              "
              style="color: #764bc4"
              >β</span
            >
            <v-icon
              v-if="
                user.moderator &&
                !user.administrator &&
                user.plan.internalName.indexOf('CSTM_TIER_')
              "
              aria-hidden="false"
              color="green"
            >
              mdi-shield
            </v-icon>
            <span
              :style="
                'text-decoration: none; color:' + calculateColor(user) + ';'
              "
              :to="'/u/' + user.username"
              ><template
                v-if="
                  user.moderator ||
                  user.administrator ||
                  user.plan.internalName.indexOf('CSTM_TIER_') ||
                  user.plan.internalName !== 'FREE'
                "
                >&nbsp;
                <router-link
                  v-if="$store.state.user.username"
                  :to="'/u/' + user.username"
                  style="text-decoration: none"
                  >{{ user.username }}</router-link
                >
                <span v-if="!$store.state.user.username">{{
                  user.username
                }}</span>
              </template>
              <v-divider class="mx-4"></v-divider>
              <v-card-text class="text-center">
                <v-chip
                  v-if="type === 'paste'"
                  class="mr-2"
                  color="indigo"
                  text-color="white"
                  @click="copyPaste()"
                >
                  <v-icon left> mdi-content-copy </v-icon>
                  Copy Paste
                </v-chip>
                <v-chip
                  class="mr-2"
                  color="teal"
                  text-color="white"
                  @click="copyLink()"
                >
                  <v-icon left> mdi-link-variant </v-icon>
                  Copy Link
                </v-chip>
                <v-chip
                  v-if="type !== 'paste'"
                  :href="'/i/' + attachment"
                  class="mr-2"
                  color="deep-purple"
                  text-color="white"
                >
                  <v-icon left> mdi-download </v-icon>
                  Download
                </v-chip>
                <v-chip
                  v-if="type === 'paste'"
                  :href="'/i/' + attachment"
                  class="mr-2"
                  color="deep-purple"
                  text-color="white"
                >
                  <v-icon left> mdi-download </v-icon>
                  Raw
                </v-chip>
                <v-chip
                  class="mr-2"
                  color="red"
                  text-color="white"
                  @click="report.modal = true"
                >
                  <v-icon left> mdi-flag </v-icon>
                  Report
                </v-chip>
              </v-card-text>
            </span>
          </span>
        </v-container>
        <v-container v-if="loading" class="text-center">
          <span class="text-h4"> Loading </span>
        </v-container>
      </v-card>
    </v-container>
    <v-container v-if="invalid">
      <v-card class="rounded-lg" elevation="7">
        <v-card-title class="text-h4 justify-center"
          >Page not Found.
        </v-card-title>
      </v-card>
    </v-container>
  </div>
</template>

<script>
import AjaxErrorHandler from "@/lib/errorHandler"

export default {
  name: "Item",
  data() {
    return {
      item: "",
      loading: true,
      name: "",
      attachment: "",
      id: 0,
      user: null,
      type: "",
      invalid: false,
      data: {},
      report: {
        modal: false,
        message: "",
        loading: false
      }
    }
  },
  methods: {
    calculateColor(user) {
      if (this.$vuetify.theme.dark) {
        return "#ffffff"
      } else {
        return "#000000"
      }
    },
    convert(binary) {
      let zeroWidth = binary
        .split("")
        .map(function (char) {
          if (char === "​") {
            return "1"
          } else if (char === "‌") {
            return "0"
          }
          return " "
        })
        .join("")
      return zeroWidth
        .split(" ")
        .map(function (num) {
          return String.fromCharCode(parseInt(num, 2))
        })
        .join("")
    },
    copyLink() {
      navigator.clipboard.writeText(
        "https://" +
          this.$store.state.user.domain.domain +
          "/" +
          this.attachment
      )
    },
    copyPaste() {
      navigator.clipboard.writeText(this.data.paste.content)
    },
    test() {
      console.log("hello")
    },
    getItem() {
      this.loading = true
      this.axios
        .get("/api/v1/gallery/attachment/" + this.$route.params.item)
        .then((res) => {
          this.loading = false
          this.name = res.data.name
          this.attachment = res.data.attachment
          this.user = res.data.User
          this.id = res.data.id
          this.type = res.data.type
          if (res.data.type === "paste") {
            this.data = JSON.parse(res.data.data)
            this.$store.commit("setRoute", "Paste")
          }
        })
        .catch((e) => {
          this.invalid = true
        })
    },
    doReport() {
      this.report.loading = true
      if (this.$store.state.user.username) {
        this.axios
          .post("/api/v1/report/auth", {
            attachment: this.id,
            message: this.report.message
          })
          .then(() => {
            this.report.modal = false
            this.report.message = ""
          })
          .catch((e) => {
            AjaxErrorHandler(this.$store)(e)
          })
      } else {
        this.axios
          .post("/api/v1/report", {
            attachment: this.id,
            message: this.report.message
          })
          .then(() => {
            this.report.modal = false
            this.report.message = ""
          })
          .catch((e) => {
            AjaxErrorHandler(this.$store)(e)
          })
      }
    }
  },
  mounted() {
    this.item = this.convert(this.$route.params.item)
    this.getItem()
  }
}
</script>

<style scoped></style>
